import { x25519 } from '@noble/curves/ed25519';
import {
  decryptAesGcm,
  encryptAesGcm,
  hkdfSha256,
  importAesKey,
  packEncryptedBlob,
  randomBytes,
  unpackEncryptedBlob,
} from './primitives';

const WRAPPED_KEY_VERSION = 0x01;
const HKDF_INFO = 'shareweb:wrap:v1';

function concatBytes(...parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const output = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    output.set(part, offset);
    offset += part.length;
  }
  return output;
}

export async function wrapKey(
  keyToWrap: Uint8Array,
  recipientPublicKey: Uint8Array,
  contextAad: string,
): Promise<Uint8Array> {
  return wrapKeyWithEphemeral(keyToWrap, recipientPublicKey, contextAad, x25519.utils.randomPrivateKey());
}

export async function wrapKeyWithEphemeral(
  keyToWrap: Uint8Array,
  recipientPublicKey: Uint8Array,
  contextAad: string,
  ephemeralPrivateKey: Uint8Array,
): Promise<Uint8Array> {
  if (keyToWrap.length !== 32 || recipientPublicKey.length !== 32) {
    throw new Error('invalid_key_length');
  }

  const ePub = x25519.getPublicKey(ephemeralPrivateKey);
  const sharedSecret = x25519.getSharedSecret(ephemeralPrivateKey, recipientPublicKey);
  const hkdfSalt = concatBytes(ePub, recipientPublicKey);
  const wrapKeyBytes = await hkdfSha256(sharedSecret, hkdfSalt, HKDF_INFO, 32);
  const aesKey = await importAesKey(wrapKeyBytes);
  const aad = new TextEncoder().encode(contextAad);
  const encrypted = await encryptAesGcm(aesKey, keyToWrap, aad);
  const packed = packEncryptedBlob(encrypted);

  const output = new Uint8Array(1 + 32 + packed.length);
  output[0] = WRAPPED_KEY_VERSION;
  output.set(ePub, 1);
  output.set(packed, 33);
  return output;
}

export async function unwrapKey(
  wrappedKey: Uint8Array,
  recipientPrivateKey: Uint8Array,
  contextAad: string,
): Promise<Uint8Array> {
  if (wrappedKey.length < 34 || wrappedKey[0] !== WRAPPED_KEY_VERSION) {
    throw new Error('unsupported_wrapped_key_version');
  }

  const ePub = wrappedKey.slice(1, 33);
  const packed = wrappedKey.slice(33);
  const recipientPublicKey = x25519.getPublicKey(recipientPrivateKey);
  const sharedSecret = x25519.getSharedSecret(recipientPrivateKey, ePub);
  const hkdfSalt = concatBytes(ePub, recipientPublicKey);
  const wrapKeyBytes = await hkdfSha256(sharedSecret, hkdfSalt, HKDF_INFO, 32);
  const aesKey = await importAesKey(wrapKeyBytes);
  const aad = new TextEncoder().encode(contextAad);
  return decryptAesGcm(aesKey, unpackEncryptedBlob(packed), aad);
}

export function generateEphemeralKeyPair(): { privateKey: Uint8Array; publicKey: Uint8Array } {
  const privateKey = randomBytes(32);
  return { privateKey, publicKey: x25519.getPublicKey(privateKey) };
}
